import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { usePWAInstall } from '../hooks/usePWAInstall';
import Header from '../components/ui/Header';

const Download = () => {
  const { isInstallable, isInstalled, promptInstall } = usePWAInstall();
  const [isIOS, setIsIOS] = useState(false);

  useEffect(() => {
    // Detect iOS to show manual installation instructions since iOS Safari doesn't support beforeinstallprompt
    const userAgent = window.navigator.userAgent.toLowerCase();
    const isIOSDevice = /iphone|ipad|ipod/.test(userAgent);
    setIsIOS(isIOSDevice && !window.navigator.standalone);
  }, []);

  return (
    <div className="min-h-screen bg-gray-50 font-sans text-gray-900 selection:bg-black selection:text-white flex flex-col">
      <Header />
      
      <main className="flex-grow flex items-center justify-center p-4">
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="max-w-md w-full bg-white rounded-3xl shadow-xl overflow-hidden border border-gray-100"
        >
          {/* Hero Art */}
          <div className="h-48 bg-gradient-to-br from-blue-900 via-slate-900 to-black relative flex items-center justify-center overflow-hidden">
            <div className="absolute inset-0 opacity-30 mix-blend-overlay" style={{ backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.8' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E")` }}></div>
            <motion.img 
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ delay: 0.2, duration: 0.5 }}
              src="https://ewz2g94uc42.exactdn.com/wp-content/uploads/2026/07/cropped-sulitstore.png" 
              alt="App Icon" 
              className="w-24 h-24 rounded-2xl shadow-2xl relative z-10 border border-white/10"
            />
          </div>

          <div className="p-8 text-center">
            <h1 className="text-2xl font-serif font-bold text-gray-900 mb-2">Sulit.ph Magazine</h1>
            <p className="text-gray-500 mb-8 text-sm leading-relaxed">
              Get the ultimate lifestyle, buys, and decisions guide right on your home screen. Fast, lightweight, and always up to date.
            </p>

            <div className="space-y-4">
              {isInstalled ? (
                <div className="bg-green-50 border border-green-100 text-green-700 py-4 px-6 rounded-2xl flex items-center justify-center gap-2">
                  <SafeIcon icon={FiIcons.FiCheckCircle} className="text-xl" />
                  <span className="font-bold">App is already installed</span>
                </div>
              ) : isIOS ? (
                <div className="bg-gray-50 border border-gray-200 p-6 rounded-2xl text-left">
                  <h3 className="font-bold text-gray-900 mb-3 flex items-center gap-2">
                    <SafeIcon icon={FiIcons.FiSmartphone} /> Install on iOS
                  </h3>
                  <ol className="text-sm text-gray-600 space-y-3">
                    <li className="flex items-start gap-2">
                      <span className="bg-white border border-gray-200 rounded-full w-5 h-5 flex items-center justify-center text-xs font-bold flex-shrink-0 mt-0.5">1</span>
                      Tap the <SafeIcon icon={FiIcons.FiShare} className="inline mx-1 text-blue-500" /> Share button in Safari's menu bar.
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="bg-white border border-gray-200 rounded-full w-5 h-5 flex items-center justify-center text-xs font-bold flex-shrink-0 mt-0.5">2</span>
                      Scroll down and tap <strong>"Add to Home Screen"</strong> <SafeIcon icon={FiIcons.FiPlusSquare} className="inline mx-1" />
                    </li>
                  </ol>
                </div>
              ) : isInstallable ? (
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={promptInstall}
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white font-bold py-4 px-6 rounded-2xl shadow-lg shadow-blue-600/30 transition-all flex items-center justify-center gap-2"
                >
                  <SafeIcon icon={FiIcons.FiDownload} className="text-xl" />
                  Install App Now
                </motion.button>
              ) : (
                <div className="bg-gray-50 border border-gray-200 text-gray-600 py-4 px-6 rounded-2xl text-sm">
                  To install, open this page in your device's native browser (Chrome on Android, Safari on iOS).
                </div>
              )}

              <Link to="/" className="block w-full py-4 text-sm font-bold text-gray-500 hover:text-gray-900 transition-colors">
                Continue to Website
              </Link>
            </div>
          </div>
        </motion.div>
      </main>
    </div>
  );
};

export default Download;